import { useMemo } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';

import type { UseDynamicTasksReturn } from '../../types';

import { DEFAULT_TASKS_DISPLAY_COUNT, DYNAMIC_TASKS_EXPANDED_STORAGE_KEY, getToggleTaskListData } from './model';
import { buildTaskShapes, getDisplayedTasks, getTaskStats, parsePersonalizedTasks } from './taskShapes';

/**
 * Hook that provides dynamic (AI-generated) tasks data.
 */
export function useDynamicTasks(): UseDynamicTasksReturn {
	const { data } = useStarterDashboardV2PageData();
	const { generationStatus, tasks } = parsePersonalizedTasks(data.petition.personalizedTasks);
	const allTasks = useMemo(() => buildTaskShapes(tasks), [tasks]);

	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(DYNAMIC_TASKS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';
	const displayCount = taskListIsExpanded ? allTasks.length : DEFAULT_TASKS_DISPLAY_COUNT;

	const { nextExpandedStorageValue, ...toggleData } = getToggleTaskListData(allTasks, taskListIsExpanded);
	const displayedTasks = useMemo(() => getDisplayedTasks(allTasks, displayCount), [allTasks, displayCount]);
	const stats = useMemo(() => getTaskStats(allTasks), [allTasks]);

	return {
		data: { tasks: displayedTasks, stats, generationStatus, taskListIsExpanded, ...toggleData },
		actions: { toggleTaskList: () => setExpandedStorageValue(nextExpandedStorageValue) },
	};
}
